// ============================================================
// Discipline History Modal Component
// ============================================================

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Chip,
  IconButton,
  Avatar,
  Paper,
  CircularProgress,
  Divider,
} from '@mui/material';
import { X, History, User, Calendar, CheckCircle2, AlertTriangle, AlertCircle, ShieldOff } from 'lucide-react';
import { useStaffDisciplineHistory } from '@/hooks/useStaffManagement';
import { EmptyState } from '@/components/feedback/EmptyState';
import { STATUS_CONFIG } from './UpdateDisciplineModal';

function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return dateStr;
  }
}

export function DisciplineHistoryModal({ open, onClose, staff }) {
  const staffId = staff?.id;
  const { data: historyData, isLoading, error } = useStaffDisciplineHistory(open ? staffId : null);

  const records = historyData?.records || [];
  const staffInfo = historyData?.staff || staff;
  const staffName = staff
    ? `${staff.first_name || ''} ${staff.last_name || ''}`.trim() || staff.name || 'Staff Member'
    : '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: 'primary.light',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <History size={20} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Discipline History
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              Full audit trail of staff conduct updates
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <X size={18} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 2.5, py: 2 }}>
        {/* Staff Summary */}
        {staff && (
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 2.5,
              bgcolor: 'action.hover',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                src={staff.photo_url || staff.avatar_url || staff.profile_picture}
                sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontWeight: 600, fontSize: 16 }}
              >
                {staffName.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {staffName}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.2 }}>
                  Employee Code: <strong>{staffInfo?.employee_code || staff.employee_code || 'N/A'}</strong>
                </Typography>
              </Box>
            </Box>
            <Chip
              label={`${records.length} ${records.length === 1 ? 'Record' : 'Records'}`}
              size="small"
              sx={{
                bgcolor: 'primary.light',
                color: 'primary.main',
                fontWeight: 700,
                fontSize: '0.75rem',
              }}
            />
          </Paper>
        )}

        {/* Loading / Error / Content */}
        {isLoading ? (
          <Box sx={{ py: 5, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Typography color="error" variant="body2" sx={{ py: 3, textAlign: 'center' }}>
            Failed to load discipline history: {error.message}
          </Typography>
        ) : records.length === 0 ? (
          <EmptyState
            title="No Discipline Records"
            description="No past discipline updates recorded for this staff member."
          />
        ) : (
          <Stack spacing={2}>
            {records.map((rec, index) => {
              const chipCfg = STATUS_CONFIG[rec.status] || STATUS_CONFIG.SATISFACTORY;
              const IconComp = chipCfg.icon;
              const authorName = rec.marked_by
                ? `${rec.marked_by.first_name || ''} ${rec.marked_by.last_name || ''}`.trim() || 'Administrator'
                : 'Administrator';

              return (
                <Paper
                  key={rec.id || index}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderLeft: '4px solid',
                    borderLeftColor: chipCfg.borderColor,
                    bgcolor: 'background.paper',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
                  >
                    {/* Filled Status Badge */}
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.8,
                        px: 1.25,
                        py: 0.4,
                        borderRadius: 1.5,
                        bgcolor: chipCfg.bgColor,
                        color: chipCfg.textColor,
                        border: `1px solid ${chipCfg.borderColor}55`,
                      }}
                    >
                      <IconComp size={14} style={{ color: chipCfg.textColor }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem', lineHeight: 1 }}>
                        {chipCfg.label}
                      </Typography>
                    </Box>

                    {/* Timestamp */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, color: 'text.secondary' }}>
                      <Calendar size={13} />
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                        {formatDate(rec.marked_at)}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Remarks Box */}
                  <Box
                    sx={{
                      p: 1.25,
                      my: 1,
                      bgcolor: 'action.hover',
                      borderRadius: 1.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: rec.remarks ? 'text.primary' : 'text.secondary',
                        fontStyle: rec.remarks ? 'normal' : 'italic',
                        fontSize: '0.825rem',
                        lineHeight: 1.4,
                      }}
                    >
                      {rec.remarks || 'No remarks recorded for this update.'}
                    </Typography>
                  </Box>

                  {/* Author / Marked By */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, pt: 0.5 }}>
                    <User size={12} style={{ color: '#6B7280' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                      Recorded by: <strong>{authorName}</strong>
                    </Typography>
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined" color="inherit" size="small" sx={{ textTransform: 'none', fontWeight: 600 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
